import React, { VoidFunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'record-it-components/form/FormGroup/FormGroup';
import {
  BufferedTextInput,
  TextInputType
} from 'record-it-components/input/TextInput/TextInput';
import { Checkbox } from 'record-it-components/input/Checkbox/Checkbox';
import {
  SingleTabEditDialog,
  SingleTabEditDialogProps
} from 'record-it-components/modal/SingleTabEditDialog/SingleTabEditDialog';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { User } from '../../../classes/entities/User/userTypes';
import { useEntityPropertyChangedCallback } from '../../../hooks/entity/useEntityPropertyChangedCallback';
import { useModifyEntityCallback } from '../../../hooks/entity/useModifyEntityCallback';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

export type EditUserDialogProps = PropsWithoutChildren<
  Omit<SingleTabEditDialogProps, 'tabTitle'>
> & {
  user: User;
};

export const EditUserDialog: VoidFunctionComponent<EditUserDialogProps> =
  React.memo((props) => {
    const { user, ...remainingProps } = prepareProps({
      props,
      ignoreChildren: true
    });

    const { currentUser } = useCurrentUser();
    const isCurrentUser = user === currentUser;

    const handlePropertyChanged = useModifyEntityCallback(EntityName.User);

    const { t } = useTranslation();
    const handleEmailChanged = useEntityPropertyChangedCallback(
      user,
      'email',
      handlePropertyChanged
    );
    const handleNameChanged = useEntityPropertyChangedCallback(
      user,
      'name',
      handlePropertyChanged
    );
    const handleAdminChanged = useEntityPropertyChangedCallback(
      user,
      'admin',
      handlePropertyChanged
    );

    return (
      <SingleTabEditDialog
        tabTitle={t('components.user.EditUserDialog.title')}
        {...remainingProps}
      >
        <FormGroup>
          <BufferedTextInput
            label={t('entities.User.email')}
            value={user.email}
            onInput={handleEmailChanged}
            type={TextInputType.Email}
            data-testid="EditUserDialog.EmailInput"
          ></BufferedTextInput>
        </FormGroup>
        <FormGroup>
          <BufferedTextInput
            label={t('entities.User.name')}
            value={user.name}
            onInput={handleNameChanged}
            type={TextInputType.Text}
            data-testid="EditUserDialog.NameInput"
          ></BufferedTextInput>
        </FormGroup>
        <FormGroup>
          <Checkbox
            label={t('components.user.EditUserDialog.admin')}
            value={user.admin}
            onValueChanged={handleAdminChanged}
            disabled={isCurrentUser}
            data-testid="EditUserDialog.AdminCheckbox"
          ></Checkbox>
        </FormGroup>
      </SingleTabEditDialog>
    );
  });
